import { memo, startTransition, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { alternateStreamUrls } from "../library/streamAlternates.js";
import { tracePlay } from "../player/playTrace.js";
import { useLocation, useNavigate } from "react-router";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { SHELF_IDS, SHELF_MAX_PAGE, SHELF_TITLES, type ShelfId } from "@hybrid-iptv/tmdb-client";
import type { Channel, SeriesItem, VodItem, WatchProgressEntry } from "@hybrid-iptv/types";
import { heroScope, KEPT_HERO_KEY, readKeptHero, type KeptHero } from "../home/keptHero.js";
import { Button, Rail, Section, Skeleton } from "@hybrid-iptv/ui";
import { useChannels, useEpgFor, useProgramIndex } from "../epg/hooks.js";
import { useActiveProviderSourceId, activeProviderSourceId } from "../epg/api.js";
import { mirrorKeyFor } from "../library/mirrorResolver.js";
import { ProviderSetupPrompt, isProviderNotConfigured } from "../components/ProviderSetupPrompt.js";
import { api } from "../api/client.js";
import {
  firstTrailerUrl,
  patchTranscodedRuntime,
  useProviderMovieDetails,
  useTmdbDetails,
  youtubeEmbedUrl,
} from "../library/vodHooks.js";
import { movieTitleFields } from "../library/titleFields.js";
import { usesTmdb } from "../discovery/rowSupplier.js";
import { useOpenHeroDetails, useOpenRowLists, type OpenRowLists } from "../home/openRows.js";
import { useDiscoveryRows, type OpenDiscovery } from "../home/discoveryRows.js";
import { useRecommended, type RecommendedItem } from "../home/recommended.js";
import { isResumable, progressKey, stillPlayable, useProgressStore, watchProgressForProfile } from "../library/progressStore.js";
import { useProfiles } from "../settings/profileStore.js";
import { artworkKey, artworkRequestFor, useHistoryArtwork, type ArtworkRequest } from "../home/historyArtwork.js";
import { useFavoritesStore } from "../library/favoritesStore.js";
import { useFavoriteRefs, useHiddenChannelIds } from "../library/useFavorites.js";
import { TitleDetail } from "../library/TitleDetail.js";
import { TrailerButton } from "../library/TrailerModal.js";
import { usePlayerStore, type RecentChannel } from "../player/playerStore.js";
import { useHomeFocus } from "../home/homeFocus.js";
import { launchReady } from "../launchDone.js";
import { useDvrSourceSync } from "../dvr/dvrSync.js";
import { useExternalLiveNow, useNowClock, type ExternalLiveNowTile } from "../home/externalLiveNow.js";
import { eligibleChannels } from "../home/liveNowPicks.js";
import { useViewerCountry } from "../settings/countryStore.js";
import { matchTmdbTitles, type TitleIndex } from "../home/catalogTitleMatch.js";
import { useHomeCatalog, type ShelfFill } from "../home/useHomeCatalog.js";
import { useAppearance } from "../library/appearanceStore.js";
import { displayTitle, localDayIndex, seededShuffle } from "../home/shelfFill.js";
import { cn } from "@hybrid-iptv/ui";
import { tilePoster } from "../library/posterSize.js";
import { brand } from "../brand.js";
import { tileLetter } from "../library/tileLetter.js";
import { useLauncherHome } from "../launcher/useLauncherHome.js";
import { LauncherRows } from "../launcher/LauncherRows.js";

interface TmdbSummaryShape {
  id: number;
  title: string;
  overview?: string;
  poster_path?: string | null;
  backdrop_path?: string | null;
  release_date?: string | null;
  vote_average?: number;
  genre_ids?: number[];
  /** A catalog title topping up a genre row, or an Open row's title: the provider's own poster URL. */
  posterUrl?: string;
  /** An Open hero title's backdrop (Section 26): the provider's, from its details. */
  backdropUrl?: string;
}

/** A genre row with fewer TMDB titles than this is topped up from the catalog. */
const MIN_SHELF = 12;
/** Channels in the Recently Watched row. */
const RECENT_ROW_SIZE = 12;
const IMG = (path: string | null | undefined, size: "w185" | "w342" | "w500" | "w780") =>
  path ? `https://image.tmdb.org/t/p/${size}${path}` : undefined;

/**
 * A title's rating in the tile's top-right corner (owner, 2026-10-10: "not huge
 * but easily visible"). Out of 10, from TMDB or the provider; none when unrated.
 */
function TileRating({ value }: { value: number | undefined }) {
  if (!value || !Number.isFinite(value) || value <= 0) return null;
  return (
    <span className="home-tile__rating" aria-hidden="true">
      <span className="home-tile__star">★</span>
      {Math.min(10, value).toFixed(1)}
    </span>
  );
}

/** Format an ISO timestamp as a compact US time, e.g. "7:30 PM". */
/**
 * The hero's muted, looping trailer — loaded only while Home is on screen.
 *
 * Home is KEPT while another page shows (App.tsx, <Activity>), hidden rather
 * than destroyed, so Back returns to it instantly. A hidden iframe keeps
 * playing, and this YouTube player is the one that starved the 1 GB projector,
 * so it is emptied when Home hides and reloaded when it shows. Effects are what
 * Activity stops on hide, so the switch happens at the moment Home hides —
 * not on a deferred re-render of a tree nobody is looking at.
 */
function HeroTrailer({ src }: { src: string }) {
  const frame = useRef<HTMLIFrameElement>(null);
  useEffect(() => {
    const el = frame.current;
    if (!el) return;
    el.src = src;
    return () => {
      el.src = "about:blank";
    };
  }, [src]);
  return (
    <iframe
      ref={frame}
      className="hero__video"
      title=""
      tabIndex={-1}
      aria-hidden="true"
      allow="autoplay; encrypted-media; picture-in-picture"
    />
  );
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  let h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${m} ${ampm}`;
}

/** Format a program's start–end range, e.g. "7:00 PM – 8:00 PM". */
export function formatProgramRange(start: string, end: string): string {
  const s = formatTime(start);
  const e = formatTime(end);
  if (!s || !e) return s || e;
  return `${s} – ${e}`;
}

/**
 * Live Now tile art with a robust fallback chain: TVmaze/TMDB artwork →
 * channel logo → letter chip. An `onError` on each <img> steps down the
 * chain when a URL 404s or the provider blocks the request, so a broken
 * poster never leaves a bare card.
 */
export function LiveTileArt({ tile }: { tile: ExternalLiveNowTile }) {
  const [artFailed, setArtFailed] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);

  if (tile.imageUrl && !artFailed) {
    return (
      <img
        className="home-tile__art"
        src={tilePoster(tile.imageUrl)}
        alt=""
        loading="lazy"
        decoding="async"
        onError={() => setArtFailed(true)}
      />
    );
  }
  if (tile.channel.logoUrl && !logoFailed) {
    return (
      <img
        className="home-tile__logo"
        src={tile.channel.logoUrl}
        alt=""
        loading="lazy"
        onError={() => setLogoFailed(true)}
      />
    );
  }
  return <span className="home-tile__fallback">{tileLetter(tile.channel.name, "?")}</span>;
}

/** One picture a history tile may show; a logo is contained, artwork is cropped. */
interface ArtCandidate {
  src: string | null | undefined;
  logo?: boolean;
}

/**
 * History-rail art with LiveTileArt's step-down: the first candidate that loads,
 * else the title's first letter — never the browser's broken-image icon.
 *
 * It remembers WHICH urls failed, not a position in the list. The TMDB picture
 * arrives after the saved one may already have failed and been skipped, and a
 * position would then point past it; a failed-set lets it take its turn.
 */
function StepDownArt({ candidates, fallback }: { candidates: ArtCandidate[]; fallback: string }) {
  const [failed, setFailed] = useState<ReadonlySet<string>>(() => new Set());
  const pick = candidates.find((candidate) => candidate.src && !failed.has(candidate.src));
  if (!pick?.src) return <span className="home-tile__fallback">{fallback}</span>;
  const src = pick.src;
  return (
    <img
      key={src}
      className={pick.logo ? "home-tile__logo" : "home-tile__art"}
      src={pick.logo ? src : tilePoster(src)}
      alt=""
      loading="lazy"
      decoding="async"
      onError={() => setFailed((previous) => new Set(previous).add(src))}
    />
  );
}

type HomeMedia = "movie" | "tv";

/**
 * The last trending/popular lists, kept on the device, so Home opens on its
 * real hero and rails instead of waiting on TMDB. WHY (projector,
 * 2026-09-26): the owner wants the launch overlay to stay until Home is
 * ready AND Home to open on its content, and a cold start's ~25 TMDB
 * requests took five seconds together. The kept copy shows at once and is
 * refreshed in the background (it is older than staleTime), as before.
 */
const TMDB_LIST_KEY = "slq.tmdbList.";

function keptList(key: string): { at: number; data: { results: TmdbSummaryShape[] } } | undefined {
  try {
    const kept = JSON.parse(localStorage.getItem(TMDB_LIST_KEY + key) ?? "null") as { at?: unknown; data?: { results?: unknown } } | null;
    if (!kept || typeof kept.at !== "number" || !Array.isArray(kept.data?.results)) return undefined;
    return kept as { at: number; data: { results: TmdbSummaryShape[] } };
  } catch {
    return undefined;
  }
}

function useTmdbList(kind: "trending" | "popular", media: HomeMedia, window: "day" | "week" = "week", enabled = true) {
  const key = `${kind}.${media}.${window}`;
  return useQuery({
    enabled,
    queryKey: ["tmdb", kind, media, window],
    queryFn: async () => {
      const data = await api.post<{ results: TmdbSummaryShape[] }>(`/api/tmdb/${kind}`, { mediaType: media, ...(kind === "trending" ? { window } : {}) });
      try {
        localStorage.setItem(TMDB_LIST_KEY + key, JSON.stringify({ at: Date.now(), data }));
      } catch {
        // Without storage the next launch simply waits for TMDB, as before.
      }
      return data;
    },
    staleTime: 1000 * 60 * 60,
    initialData: () => (enabled ? keptList(key)?.data : undefined),
    initialDataUpdatedAt: () => (enabled ? keptList(key)?.at : undefined),
  });
}

type RowList = { data?: { results: TmdbSummaryShape[] }; isLoading: boolean };

/**
 * One of Home's lists from whichever supplier gives the rows (Section 26):
 * TMDB's query exactly as before, or Open's list from the catalog.
 */
function useRowList(tmdb: RowList, open: { lists?: OpenRowLists; isLoading: boolean }, kind: keyof OpenRowLists, fromTmdb: boolean): RowList {
  const openData = useMemo(() => (open.lists ? { results: open.lists[kind] as TmdbSummaryShape[] } : undefined), [open.lists, kind]);
  const openList = useMemo(() => ({ data: openData, isLoading: open.isLoading }), [openData, open.isLoading]);
  return fromTmdb ? tmdb : openList;
}

/**
 * TMDB's US titles for each genre row: page 1 (the titles everyone knows) and
 * one more page that changes daily, so the rows turn over without leaving the
 * well-known titles behind.
 */
interface ShelvesInput {
  railReady: boolean;
  listsLoaded: boolean;
  stamp: string;
  byShelf: Map<ShelfId, TmdbSummaryShape[]>;
  filteredTrending: TmdbSummaryShape[];
  filteredPopular: TmdbSummaryShape[];
  dayIndex: number;
  media: HomeMedia;
  seriesById: Map<number, SeriesItem>;
  seriesTitleIndex: TitleIndex<SeriesItem>;
  moviesById: Map<number, VodItem>;
  movieTitleIndex: TitleIndex<VodItem>;
  fillShelf: ShelfFill;
  tilesPerRow: number;
  /** A row with fewer titles than this is topped up from the catalog. */
  fillTo: number;
}

interface Shelves {
  rows: Array<{ shelf: ShelfId; title: string; items: TmdbSummaryShape[] }>;
  matched: Map<number, VodItem | SeriesItem>;
}

const NO_SHELVES: Shelves = { rows: [], matched: new Map() };
const NO_SHELF_ROWS: Shelves["rows"] = [];

/**
 * Genre rows (Family, Drama, Kids, Action & Adventure, Comedy, Sci-Fi,
 * Romance): TMDB's US titles for the genre that the provider has, in a daily
 * order, topped up from the catalog's own genre categories when too few match
 * (home/shelfFill.ts). No title appears twice on Home. The top-up is asked for
 * (natively in the APK), so the rows are built asynchronously and in order:
 * each row's picks are skipped by the rows after it.
 */
function useShelves(input: ShelvesInput): Shelves {
  const [shelves, setShelves] = useState<Shelves>(NO_SHELVES);
  const { railReady, listsLoaded, stamp, filteredTrending, filteredPopular, dayIndex, media, seriesById, seriesTitleIndex, moviesById, movieTitleIndex, fillShelf, tilesPerRow, fillTo } = input;
  const byShelf = input.byShelf;
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const rowsOut: Shelves["rows"] = [];
      const matched = new Map<number, VodItem | SeriesItem>();
      if (railReady && listsLoaded) {
        const used = new Set<number>([...filteredTrending, ...filteredPopular].map((t) => t.id));
        for (const shelf of SHELF_IDS) {
          const seed = `${dayIndex}:${media}:${shelf}`;
          const tmdb = byShelf.get(shelf) ?? [];
          const found: Map<number, VodItem | SeriesItem> = media === "tv"
            ? matchTmdbTitles(tmdb, seriesById, seriesTitleIndex, "tv")
            : matchTmdbTitles(tmdb, moviesById, movieTitleIndex, "movie");
          const items: TmdbSummaryShape[] = [];
          for (const title of seededShuffle(tmdb, seed, (t) => t.id)) {
            if (items.length === tilesPerRow) break;
            if (!found.has(title.id) || used.has(title.id)) continue;
            used.add(title.id);
            matched.set(title.id, found.get(title.id)!);
            items.push(title);
          }
          if (items.length < fillTo) {
            let fill: Array<VodItem | SeriesItem> = [];
            try {
              fill = await fillShelf(shelf, used, fillTo - items.length, seed);
            } catch {
              // A row without its top-up is still a row.
            }
            if (cancelled) return;
            for (const entry of fill) {
              used.add(entry.tmdbId!);
              items.push({
                id: entry.tmdbId!,
                title: displayTitle(entry.rawTitle),
                ...(entry.posterUrl ? { posterUrl: entry.posterUrl } : {}),
                ...(entry.rating !== undefined ? { vote_average: entry.rating } : {}),
              });
            }
          }
          if (items.length > 0) rowsOut.push({ shelf, title: SHELF_TITLES[shelf], items });
        }
      }
      if (!cancelled) setShelves({ rows: rowsOut, matched });
    })();
    return () => {
      cancelled = true;
    };
    // stamp stands in for byShelf, which is rebuilt every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [railReady, listsLoaded, stamp, filteredTrending, filteredPopular, dayIndex, media, seriesById, seriesTitleIndex, moviesById, movieTitleIndex, fillShelf, tilesPerRow, fillTo]);
  return shelves;
}

function useShelfLists(media: HomeMedia, dayIndex: number, enabled = true, discovery: OpenDiscovery | null | undefined = null) {
  const rotatingPage = 2 + (dayIndex % (SHELF_MAX_PAGE - 1));
  const queries = useQueries({
    queries: SHELF_IDS.flatMap((shelf) =>
      [1, rotatingPage].map((page) => ({
        queryKey: ["tmdb-shelf", media, shelf, page],
        queryFn: () => api.post<{ results: TmdbSummaryShape[] }>("/api/tmdb/shelf", { mediaType: media, shelf, page }),
        staleTime: 1000 * 60 * 60 * 12,
        // Open (Section 26): no TMDB lists; each genre row is filled from the
        // catalog's own genre categories (useShelves' top-up) until step 4.
        enabled,
      })),
    ),
  });
  if (!enabled) {
    // Open: the discovery files' genre rows when the device has them (step 4), else empty and
    // useShelves fills each row from the catalog's own genre categories.
    if (discovery) {
      const stamp = `discovery:${[...discovery.byShelf.values()].map((row) => row.map((entry) => entry.id).join(",")).join("|")}`;
      return { byShelf: discovery.byShelf as Map<ShelfId, TmdbSummaryShape[]>, loaded: true, stamp };
    }
    return { byShelf: new Map<ShelfId, TmdbSummaryShape[]>(), loaded: discovery === null, stamp: "open" };
  }
  const byShelf = new Map<ShelfId, TmdbSummaryShape[]>();
  SHELF_IDS.forEach((shelf, i) => {
    byShelf.set(shelf, [...(queries[i * 2]?.data?.results ?? []), ...(queries[i * 2 + 1]?.data?.results ?? [])]);
  });
  const loaded = queries.every((q) => !q.isLoading);
  return { byShelf, loaded, stamp: queries.map((q) => q.dataUpdatedAt).join(",") };
}

// Which half of the TMDB rails Home shows. Remembered per viewer, so a series
// watcher is not flipped back to movies on every visit; storage can be missing
// (private window, blocked site data), so it only ever falls back to movies.
const MEDIA_STORAGE_KEY = "slique.home.media";
function loadHomeMedia(): HomeMedia {
  try {
    return window.localStorage.getItem(MEDIA_STORAGE_KEY) === "tv" ? "tv" : "movie";
  } catch {
    return "movie";
  }
}
function saveHomeMedia(media: HomeMedia): void {
  try {
    window.localStorage.setItem(MEDIA_STORAGE_KEY, media);
  } catch {
    // Not remembered; the toggle still works for this visit.
  }
}

interface SelectedTitle {
  id: number;
  title: string;
  media: HomeMedia;
  posterPath?: string;
  backdropPath?: string;
}

export function HomePage() {
  const queryClient = useQueryClient();
  // Settings > Home: what the home screen shows, and how heavy it is.
  const heroTrailer = useAppearance((s) => s.heroTrailer);
  const liveNowRow = useAppearance((s) => s.liveNowRow);
  const heroRotation = useAppearance((s) => s.heroRotation);
  const tilesPerRow = useAppearance((s) => s.tilesPerRow);
  // Launcher mode (Settings → Launcher mode, off by default): the box's apps as a row under the
  // banner; Quick settings, Android settings, the stores and the inputs are links in the top bar
  // (owner, 2026-10-20).
  const { home: launcherHome } = useLauncherHome();
  const launcherApps = launcherHome?.apps.length ?? 0;
  const navigate = useNavigate();
  const [selected, setSelected] = useState<SelectedTitle | null>(null);
  /** True when a tile's trailer shortcut opened the detail — auto-plays the trailer. */
  const [autoTrailer, setAutoTrailer] = useState(false);

  const sourceId = useActiveProviderSourceId();
  const channelsQuery = useChannels(sourceId);
  const channels = channelsQuery.data?.channels ?? [];

  // --- My List (Section 11): favorite channels/movies/series resolved against
  //     the on-device catalogs. `hydrated` gates the rail so a cold start does
  //     not flash an empty rail while IndexedDB favorites load.
  const favoritesHydrated = useFavoritesStore((s) => s.hydrated);
  const favMovieRefs = useFavoriteRefs("movie");
  const favSeriesRefs = useFavoriteRefs("series");
  const favChannelRefs = useFavoriteRefs("channel");
  const hiddenChannelIds = useHiddenChannelIds();
  const favMovieIds = useMemo(() => new Set(favMovieRefs), [favMovieRefs]);
  const favSeriesIds = useMemo(() => new Set(favSeriesRefs), [favSeriesRefs]);
  const favChannelIds = useMemo(() => new Set(favChannelRefs), [favChannelRefs]);
  const hasFavorites =
    favoritesHydrated && (favMovieRefs.length > 0 || favSeriesRefs.length > 0 || favChannelRefs.length > 0);
  // Home asks the guide for the channels it shows only (Phase 3 step 2a): the few hundred
  // Live Now could feature and the Recently Watched row, never the whole feed.
  const country = useViewerCountry();
  const recentChannels = usePlayerStore((s) => s.recentChannels);
  const guideChannels = useMemo(() => {
    const wanted = liveNowRow && channels.length > 0 ? eligibleChannels(channels, country).map((entry) => entry.channel) : [];
    const ids = new Set(wanted.map((channel) => channel.id));
    const recentIds = new Set(recentChannels.slice(0, RECENT_ROW_SIZE).map((channel) => channel.id));
    for (const channel of channels) if (recentIds.has(channel.id) && !ids.has(channel.id)) wanted.push(channel);
    return wanted;
  }, [liveNowRow, channels, country, recentChannels]);
  const epg = useEpgFor(sourceId, channels, guideChannels, 4, { keepPrevious: true });
  // The Android app's DVR learns the guide from Home too, so series rules work
  // without a visit to Live TV.
  useDvrSourceSync(sourceId, epg.data, channels);
  const programIndex = useProgramIndex(epg.data, guideChannels);
  // Ticking clock — drives the Live Now window, Recently Watched's
  // now-playing program, and the progress bars so nothing goes stale
  // mid-program (60s tick + instant refresh on tab focus).
  const now = useNowClock();
  // --- TMDB rails: the Movies/Series toggle picks which half they show. The
  //     hero always features movies (one press plays them), so it keeps its
  //     own trending-movies list; with Movies selected the two share a cache.
  const [media, setMediaState] = useState<HomeMedia>(loadHomeMedia);
  const setMedia = (next: HomeMedia) => {
    setMediaState(next);
    saveHomeMedia(next);
  };
  // Section 26: TMDB or Open supplies the lists (discovery/rowSupplier.ts). Open takes the
  // discovery files' rows (step 4) for the day, sized to the viewer's tiles per row.
  const tmdbRows = usesTmdb();
  const dayIndex = localDayIndex(now);
  const kidsProfile = useProfiles((s) => s.profiles.find((p) => p.id === s.activeProfileId)?.isKids === true);
  const discoveryMovies = useDiscoveryRows(sourceId, "movie", dayIndex, tilesPerRow, kidsProfile, !tmdbRows);
  const discoverySeries = useDiscoveryRows(sourceId, "tv", dayIndex, tilesPerRow, kidsProfile, !tmdbRows && media === "tv");
  const openMovies = useOpenRowLists(sourceId, "movie", !tmdbRows, discoveryMovies.discovery);
  const openSeries = useOpenRowLists(sourceId, "tv", !tmdbRows && media === "tv", discoverySeries.discovery);
  // External "Live Now" (home/liveNowPicks.ts): the most-watched US networks' channels, never a
  // 24/7 loop, live sports doubled, local stations only for the market the viewer's own
  // favourites and recent stations are in. Clicking a tile tunes that channel (one stream).
  const recentForMarket = usePlayerStore((s) => s.recentChannels);
  const watchedNames = useMemo(() => {
    const names = recentForMarket.map((channel) => channel.name);
    if (favChannelIds.size > 0) for (const channel of channels) if (favChannelIds.has(channel.id)) names.push(channel.name);
    return names;
  }, [recentForMarket, channels, favChannelIds]);
  const liveNow = useExternalLiveNow(channels, programIndex, liveNowRow, watchedNames);
  const openSide = media === "tv" ? openSeries : openMovies;
  const trendingMovies = useRowList(useTmdbList("trending", "movie", "week", tmdbRows), openMovies, "trending", tmdbRows);
  const trending = useRowList(useTmdbList("trending", media, "week", tmdbRows), openSide, "trending", tmdbRows);
  const popular = useRowList(useTmdbList("popular", media, "week", tmdbRows), openSide, "popular", tmdbRows);
  const shelfLists = useShelfLists(media, dayIndex, tmdbRows, (media === "tv" ? discoverySeries : discoveryMovies).discovery);

  // --- Catalog (loaded once trending arrives, a tile opens, or My List has
  //     favorited titles — resolving them needs the catalog) ---
  const vodEnabled = selected !== null || trendingMovies.data !== undefined || trending.data !== undefined || hasFavorites;
  // Movie titles are always looked up (the hero features trending movies); the
  // rest follow the Movies/Series toggle. Only these titles are asked for in the
  // APK (home/useHomeCatalog.ts), never the whole catalog.
  // Spotlight (Section 26 step 4): Open's one extra row after the genre rows, from the
  // discovery files; its titles are looked up in the catalog with the genre rows'.
  const spotlight = tmdbRows ? null : ((media === "tv" ? discoverySeries : discoveryMovies).discovery?.spotlight ?? null);
  const shelfTitles = useMemo(
    () => [...SHELF_IDS.flatMap((shelf) => shelfLists.byShelf.get(shelf) ?? []), ...((spotlight?.items ?? []) as TmdbSummaryShape[])],
    // shelfLists.stamp stands in for the per-shelf data it summarises.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shelfLists.stamp, media, spotlight],
  );
  const movieTitles = useMemo(
    () => [
      ...(trendingMovies.data?.results ?? []),
      ...(media === "movie" ? [...(trending.data?.results ?? []), ...(popular.data?.results ?? []), ...shelfTitles] : []),
    ],
    [trendingMovies.data, trending.data, popular.data, media, shelfTitles],
  );
  const tvTitles = useMemo(
    () => (media === "tv" ? [...(trending.data?.results ?? []), ...(popular.data?.results ?? []), ...shelfTitles] : []),
    [trending.data, popular.data, media, shelfTitles],
  );
  const selectedMovieIds = useMemo(() => (selected?.media === "movie" ? [selected.id] : []), [selected]);
  const selectedTvIds = useMemo(() => (selected?.media === "tv" ? [selected.id] : []), [selected]);
  const vod = useHomeCatalog({
    sourceId,
    enabled: vodEnabled,
    media,
    movieTitles,
    tvTitles,
    movieIds: selectedMovieIds,
    tvIds: selectedTvIds,
    favMovieRefs,
    favSeriesRefs,
  });
  // Map TMDB id -> playlist movie, straight from the provider's tmdbId field.
  // While the catalog loads the map is empty and rails show unfiltered; once
  // it resolves, every rail (and the hero) only features titles you can play.
  const { moviesById, seriesById, movieTitleIndex, seriesTitleIndex } = vod;
  const catalogReady = vod.moviesReady;
  // Some panels leave the TMDB id off exactly their newest titles, so a title
  // the id lookup misses is matched by title and year (home/catalogTitleMatch).
  const catalogMatches = useMemo(
    () =>
      matchTmdbTitles(
        [...(trendingMovies.data?.results ?? []), ...(media === "movie" ? [...(trending.data?.results ?? []), ...(popular.data?.results ?? [])] : [])],
        moviesById, movieTitleIndex, "movie",
      ),
    [trendingMovies.data, trending.data, popular.data, media, moviesById, movieTitleIndex],
  );
  const seriesMatches = useMemo(
    () =>
      media === "tv"
        ? matchTmdbTitles([...(trending.data?.results ?? []), ...(popular.data?.results ?? [])], seriesById, seriesTitleIndex, "tv")
        : new Map<number, SeriesItem>(),
    [media, trending.data, popular.data, seriesById, seriesTitleIndex],
  );
  const railMatches: Map<number, unknown> = media === "tv" ? seriesMatches : catalogMatches;
  // Filter once the catalog itself has loaded, even if nothing matched: an
  // empty rail is right for a panel without these titles.
  const railReady = media === "tv" ? vod.seriesReady : vod.moviesReady;

  const filteredTrending = useMemo(() => {
    const items = trending.data?.results ?? [];
    return railReady ? items.filter((t) => railMatches.has(t.id)) : items;
  }, [trending.data, railReady, railMatches]);
  const filteredPopular = useMemo(() => {
    const items = popular.data?.results ?? [];
    return railReady ? items.filter((t) => railMatches.has(t.id)) : items;
  }, [popular.data, railReady, railMatches]);

  // --- Genre rows (Family, Drama, Kids, Action & Adventure, Comedy, Sci-Fi,
  //     Romance): TMDB's US titles for the genre that the provider has, in a
  //     daily order, topped up from the catalog's own genre categories when
  //     too few match (home/shelfFill.ts). No title appears twice on Home.
  const shelves = useShelves({
    railReady,
    listsLoaded: shelfLists.loaded,
    stamp: shelfLists.stamp,
    byShelf: shelfLists.byShelf,
    filteredTrending,
    filteredPopular,
    dayIndex,
    media,
    seriesById,
    seriesTitleIndex,
    moviesById,
    movieTitleIndex,
    fillShelf: vod.fillShelf,
    tilesPerRow,
    // Open (Section 26) has no TMDB lists, so the catalog fills each row to the
    // viewer's tiles per row (Settings: 12 or 24); with TMDB, the top-up as before.
    fillTo: tmdbRows ? MIN_SHELF : tilesPerRow,
  });

  // Spotlight: exactly one full row or none (ADR: "never a half-empty row"). The device
  // picked titles no other row has; the catalog matching and the genre rows' top-up can
  // still take some, so those are left out here and a row left short is not shown.
  const spotlightRow = useMemo(() => {
    if (!spotlight || !railReady) return null;
    const onHome = new Set([...filteredTrending, ...filteredPopular, ...shelves.rows.flatMap((row) => row.items)].map((t) => t.id));
    const titles = spotlight.items as TmdbSummaryShape[];
    const found: Map<number, VodItem | SeriesItem> = media === "tv"
      ? matchTmdbTitles(titles, seriesById, seriesTitleIndex, "tv")
      : matchTmdbTitles(titles, moviesById, movieTitleIndex, "movie");
    const items = titles.filter((t) => found.has(t.id) && !onHome.has(t.id)).slice(0, tilesPerRow);
    return items.length === tilesPerRow ? { title: spotlight.title, items, matched: found } : null;
  }, [spotlight, railReady, filteredTrending, filteredPopular, shelves.rows, media, seriesById, seriesTitleIndex, moviesById, movieTitleIndex, tilesPerRow]);

  // --- My List items: favorites resolved against the catalogs, newest-first.
  const favMovies = useMemo(
    () =>
      vod.favMovies
        .filter((m) => favMovieIds.has(m.id))
        .sort((a, b) => favMovieRefs.indexOf(a.id) - favMovieRefs.indexOf(b.id)),
    [vod.favMovies, favMovieIds, favMovieRefs],
  );
  const favSeries = useMemo(
    () =>
      vod.favSeries
        .filter((s) => favSeriesIds.has(s.id))
        .sort((a, b) => favSeriesRefs.indexOf(a.id) - favSeriesRefs.indexOf(b.id)),
    [vod.favSeries, favSeriesIds, favSeriesRefs],
  );
  const favChannels = useMemo(
    () => channels.filter((c) => favChannelIds.has(c.id) && !hiddenChannelIds.has(c.id)),
    [channels, favChannelIds, hiddenChannelIds],
  );
  const myListCount = favMovies.length + favSeries.length + favChannels.length;

  // --- Continue Watching (Sections 9-10 progress store) ---
  // Select the stable entries array (NOT s.continueWatching(), which returns a
  // fresh array every call and would re-render forever) and sort in a memo.
  const progressEntries = useProgressStore((s) => s.entries);
  const playbackMeta = useProgressStore((s) => s.playbackMeta);
  const activeProfile = useProfiles((s) => s.activeProfileId);
  const continueEntries = useMemo(
    () =>
      watchProgressForProfile(progressEntries, activeProfile)
        // A catch-up replay leaves the rail once it falls out of the provider's
        // archive: past that its URL 404s, and a tile that plays nothing is
        // worse than no tile. The profile-aware metadata is part of its key.
        .filter((entry) => entry.contentType !== "catchup" || stillPlayable(playbackMeta[progressKey(activeProfile, entry.contentRef)], now)),
    [progressEntries, playbackMeta, activeProfile, now],
  );

  // --- Recently Watched (YouTube TV-style: channels you tuned, with the
  //     now-playing program from the EPG) ---
  // `now` is a dep so the now-playing program and its progress bar stay
  // current as programmes start/end, not just when the channel list or EPG
  // changes.
  const recentRows = useMemo(() => {
    if (recentChannels.length === 0) return [];
    return recentChannels.slice(0, RECENT_ROW_SIZE).map((channel) => ({
      channel,
      program: programIndex?.getNowPlaying(channel.id) ?? null,
    }));
  }, [recentChannels, programIndex, now]);

  // --- History-rail artwork (home/historyArtwork.ts): what TMDB shows for each
  //     remembered title, so these tiles get pictures like Live Now's do.
  const continueArtRequests = useMemo(
    () =>
      continueEntries.map((entry) => {
        const title = playbackMeta[progressKey(activeProfile, entry.contentRef)]?.title;
        return title ? artworkRequestFor(entry.contentType, title) : undefined;
      }),
    [continueEntries, playbackMeta, activeProfile],
  );
  const recentArtRequests = useMemo(
    () =>
      recentRows.map(({ channel, program }) =>
        // A "24/7 <show>" channel only ever plays that show, so its NAME beats
        // its guide — providers map other listings onto these (seen: "blue Sport
        // 1 Live" on 24/7 Bluey). Any other channel is searched by what is on;
        // with nothing on, a bare channel name is not a safe search.
        /^\s*24\s*\/\s*7/i.test(channel.name)
          ? artworkRequestFor("channel", channel.name)
          : program
            ? artworkRequestFor("channel", program.title)
            : undefined,
      ),
    [recentRows],
  );
  const historyArtRequests = useMemo(
    () => [...continueArtRequests, ...recentArtRequests],
    [continueArtRequests, recentArtRequests],
  );
  const historyArt = useHistoryArtwork(historyArtRequests);
  const artFor = useCallback(
    (request: ArtworkRequest | undefined) => (request ? historyArt.get(artworkKey(request)) : undefined),
    [historyArt],
  );

  // --- Hero: rotate through the top 5 trending titles actually in your
  //     playlist. The minute is derived from the shared ticking clock so the
  //     hero advances in step with the Live Now rail (60s tick + tab focus).
  const HERO_ROTATION = 5;
  const trendingInCatalog = useMemo(
    () => (catalogReady ? (trendingMovies.data?.results ?? []).filter((t) => catalogMatches.has(t.id)) : []),
    [catalogReady, trendingMovies.data, catalogMatches],
  );
  // Open (Section 26): a film's list entry has no backdrop, so the panel is asked
  // about the first few, and a film without one is left out of the hero.
  const heroFilms = useMemo(
    () => (tmdbRows ? [] : trendingInCatalog.map((t) => catalogMatches.get(t.id)).filter((m): m is VodItem => Boolean(m))),
    [tmdbRows, trendingInCatalog, catalogMatches],
  );
  const openHero = useOpenHeroDetails(heroFilms, !tmdbRows);
  const matchedTrending = useMemo(
    () =>
      tmdbRows
        ? trendingInCatalog
        : trendingInCatalog
            .filter((t) => openHero.has(t.id))
            .map((t) => {
              const answer = openHero.get(t.id)!;
              // The plot came with the backdrop: the banner's text (it was thrown away, 2026-10-12).
              return { ...t, backdropUrl: answer.backdropUrl, ...(answer.overview ? { overview: answer.overview } : {}) };
            }),
    [tmdbRows, trendingInCatalog, openHero],
  );
  const heroCount = Math.min(HERO_ROTATION, matchedTrending.length);
  // Held still when the viewer turned rotation off: each change re-renders the
  // hero and loads another backdrop.
  const heroSlot = heroCount > 0 ? (heroRotation ? Math.floor(now / 60_000) % heroCount : 0) : -1;
  const liveHeroTitle = heroSlot >= 0 ? matchedTrending[heroSlot] : undefined;
  const liveHeroItem = liveHeroTitle ? catalogMatches.get(liveHeroTitle.id) : undefined;
  // Last session's hero until the live one arrives (see KEPT_HERO_KEY).
  // Read again when the playlist changes: Home stays mounted across a switch, and the kept hero
  // read once at mount was the previous playlist's, shown on the new one until a restart (and the
  // new one's never showed) (2026-10-20).
  const keptHero = useMemo(() => (sourceId ? readKeptHero<TmdbSummaryShape>(sourceId) : undefined), [sourceId]);
  const useKept = !liveHeroItem && keptHero !== undefined;
  const heroTitle = useKept ? keptHero.title : liveHeroTitle;
  // The NEXT title in the rotation — preloaded so the swap never flashes blank.
  const nextHeroTitle = heroCount > 0 ? matchedTrending[(heroSlot + 1) % heroCount] : undefined;
  const heroItem = useKept ? keptHero.item : liveHeroItem;
  const heroBackdrop = IMG(heroTitle?.backdrop_path, "w780") ?? heroTitle?.backdropUrl;
  const nextHeroBackdrop = IMG(nextHeroTitle?.backdrop_path, "w780") ?? nextHeroTitle?.backdropUrl;
  const heroYear = heroTitle?.release_date?.slice(0, 4);
  const heroRating = heroTitle?.vote_average;
  const hasHero = !!heroTitle && !!heroItem && !!heroBackdrop;
  // Keep the live hero for the next cold start.
  useEffect(() => {
    if (!sourceId || !liveHeroTitle || !liveHeroItem || !(liveHeroTitle.backdrop_path || liveHeroTitle.backdropUrl)) return;
    try {
      localStorage.setItem(KEPT_HERO_KEY, JSON.stringify({ scope: heroScope(sourceId), title: liveHeroTitle, item: liveHeroItem } satisfies KeptHero<TmdbSummaryShape>));
    } catch {
      // Without storage the next cold start waits for the live hero, as before.
    }
  }, [sourceId, liveHeroTitle, liveHeroItem]);

  // Ready for the APK to show (launchReady): the real hero, or nothing left
  // that could bring one, and the viewer's own lists read. Until then the launch
  // overlay stays up, so Home opens on its content instead of the placeholder
  // hero with rows appearing above it (owner, 2026-09-26). The hero's picture is
  // decoded first so it does not pop in; LaunchDone caps the whole wait.
  const homeReady = favoritesHydrated && (hasHero || (!trending.isLoading && (catalogReady || !vodEnabled)));
  useEffect(() => {
    if (!homeReady) return;
    if (!heroBackdrop) {
      launchReady();
      return;
    }
    const picture = new Image();
    picture.src = heroBackdrop;
    const cap = setTimeout(launchReady, 1_500);
    void picture.decode().catch(() => undefined).then(() => {
      clearTimeout(cap);
      launchReady();
    });
    return () => clearTimeout(cap);
  }, [homeReady, heroBackdrop]);

  // Hero trailer: the provider's exact trailer URL when available, else TMDB
  // details (one cached query). Drives the hero's ▶ Trailer button and the
  // animated muted background.
  const heroDetails = useTmdbDetails("movie", heroTitle?.id);
  const heroTrailerUrl = heroItem?.trailerUrl ?? firstTrailerUrl(heroDetails.data);
  // Off by default in the APK (Settings > Home): a TV decodes this in software,
  // and the YouTube player in an iframe starved the 1 GB projector's main
  // thread — a remote key took seven seconds, and after a few of the hero's
  // minute rotations the app stopped answering at all (2026-09-20). With it
  // off the hero keeps its backdrop, and the Trailer button still plays one.
  const heroTrailerEmbed = heroTrailerUrl && heroTrailer
    ? youtubeEmbedUrl(heroTrailerUrl, { autoplay: true, mute: true, controls: false, loop: true })
    : undefined;

  // --- D-pad roving over all rows (hero, continue, recent, live, rails) ---
  // The genre rows come in just after the first paint. They sit below the fold
  // and are the bulk of Home's tiles (about 140 of them), so building them in
  // the same pass as the hero made the viewer wait for posters they could not
  // see yet (1 GB projector, 2026-09-24). A transition, so a key press made
  // meanwhile is answered first; and until they exist the remote's map of the
  // rows leaves them out, so Down never aims at a row that is not there.
  const [shelvesShown, setShelvesShown] = useState(false);
  useEffect(() => {
    if (shelvesShown) return;
    const idle = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    const show = () => startTransition(() => setShelvesShown(true));
    if (idle.requestIdleCallback) {
      const id = idle.requestIdleCallback(show, { timeout: 1000 });
      return () => idle.cancelIdleCallback?.(id);
    }
    const id = window.setTimeout(show, 100);
    return () => window.clearTimeout(id);
  }, [shelvesShown]);
  // "Recommended For You" replaces My List here (owner, 2026-10-12): per profile, worked out once
  // the first paint is done. While it is being worked out Home waits rather than flashing My List
  // and swapping; with no discovery files yet (or a test build on TMDB) My List stays.
  const recommended = useRecommended(sourceId, tilesPerRow, dayIndex, !tmdbRows && shelvesShown);
  const recommendedPending = !tmdbRows && (!shelvesShown || recommended.items === undefined);
  const recommendedItems = useMemo(() => recommended.items ?? [], [recommended.items]);
  const showRecommended = !recommendedPending && recommendedItems.length > 0;
  const showMyList = !recommendedPending && !showRecommended && myListCount > 0;
  const shownShelves = shelvesShown ? shelves.rows : NO_SHELF_ROWS;
  const shownSpotlight = shelvesShown ? spotlightRow : null;

  const rows = useMemo(() => {
    const cols = (n: number) => Math.min(n, tilesPerRow);
    const list: Array<{ id: string; cols: number }> = [{ id: "hero", cols: heroTrailerUrl ? 3 : 2 }];
    if (launcherApps > 0) list.push({ id: "apps", cols: launcherApps });
    if (showRecommended) list.push({ id: "recommended", cols: Math.min(recommendedItems.length, tilesPerRow) });
    else if (showMyList) list.push({ id: "mylist", cols: Math.min(myListCount, tilesPerRow) });
    if (liveNow.tiles.length > 0) list.push({ id: "live", cols: cols(liveNow.tiles.length) });
    if (continueEntries.length > 0) list.push({ id: "continue", cols: cols(continueEntries.length) });
    if (recentRows.length > 0) list.push({ id: "recent", cols: cols(recentRows.length) });
    list.push({ id: "media", cols: 2 });
    list.push({ id: "trending", cols: cols(filteredTrending.length) });
    list.push({ id: "popular", cols: cols(filteredPopular.length) });
    for (const row of shownShelves) list.push({ id: `shelf-${row.shelf}`, cols: cols(row.items.length) });
    if (shownSpotlight) list.push({ id: "spotlight", cols: cols(shownSpotlight.items.length) });
    return list;
  }, [launcherApps, heroTrailerUrl, showRecommended, recommendedItems.length, showMyList, myListCount, liveNow.tiles.length, continueEntries.length, recentRows.length, filteredTrending.length, filteredPopular.length, shownShelves, shownSpotlight]);
  const rowIndex = useCallback((id: string) => rows.findIndex((r) => r.id === id), [rows]);
  const { isFocused, focusedCol: focusedColOf, focusAt } = useHomeFocus(rows);
  // Launcher mode: Back with nothing left to undo comes here with a marker (ExitConfirm), and
  // Home goes to its top, the banner's first button, instead of asking to leave the app.
  const launcherTop = (useLocation().state as { launcherTop?: number } | null)?.launcherTop;
  useEffect(() => {
    if (launcherTop) focusAt(0, 0);
  }, [launcherTop, focusAt]);
  // The banner is shown whole whenever focus is on its buttons or above it in the top bar (owner,
  // 2026-10-20: "the banner needs to snap into the screen completely ... not stay cut off").
  // Moving up from the row below scrolled only far enough for the button, leaving the banner's
  // top under the bar.
  useEffect(() => {
    const onFocus = (event: FocusEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target?.closest(".hero, .topbar")) return;
      const scroller = document.querySelector<HTMLElement>(".app__content");
      if (scroller && scroller.scrollTop > 0) scroller.scrollTop = 0;
    };
    document.addEventListener("focusin", onFocus);
    return () => document.removeEventListener("focusin", onFocus);
  }, []);

  // Live Now rotation: shift the rail one slot per minute (same clock as the
  // hero) so the leading tile advances — the row never looks frozen. Held while
  // Home's place is in the row: the rotation moves the first tile to the end,
  // and when that tile had focus the browser scrolled after it, so the row
  // jumped to its end under the viewer (owner, 2026-10-06).
  const liveMinuteRef = useRef(0);
  const liveTiles = useMemo(() => {
    const tiles = liveNow.tiles;
    if (tiles.length === 0) return tiles;
    if (focusedColOf(rowIndex("live")) < 0) liveMinuteRef.current = Math.floor(now / 60_000);
    const offset = liveMinuteRef.current % tiles.length;
    return offset === 0 ? tiles : [...tiles.slice(offset), ...tiles.slice(0, offset)];
  }, [liveNow.tiles, now, focusedColOf, rowIndex]);

  // --- Detail overlay data ---
  const details = useTmdbDetails(selected?.media ?? "movie", selected?.id);
  const shelfMatch = selected ? shelves.matched.get(selected.id) ?? spotlightRow?.matched.get(selected.id) : undefined;
  const matchedItem = selected?.media === "movie"
    ? catalogMatches.get(selected.id) ?? (shelfMatch as VodItem | undefined) ?? moviesById.get(selected.id)
    : undefined;
  const matchedSeries = selected?.media === "tv"
    ? seriesMatches.get(selected.id) ?? (shelfMatch as SeriesItem | undefined) ?? seriesById.get(selected.id)
    : undefined;
  // A film in the playlist: the panel's own details first (Section 26), as on the Movies page.
  const matchedDetails = useProviderMovieDetails(matchedItem);
  const movieFields = matchedItem ? movieTitleFields(matchedItem, matchedDetails.data, details.data) : undefined;

  const tuneChannel = useCallback((channel: Channel) => {
    const store = usePlayerStore.getState();
    store.pushRecent({ id: channel.id, name: channel.name, number: channel.number, streamUrl: channel.streamUrl, altStreamUrls: channel.altStreamUrls });
    void store.playSource({ url: channel.streamUrl, type: "live", title: channel.name, altUrls: channel.altStreamUrls, mirrorKey: mirrorKeyFor(activeProviderSourceId(), channel.id) });
  }, [])

  // My List movie tile: open the detail overlay (like the Trending/Popular
  // rails) when the title carries a TMDB id; otherwise hand off to the Movies
  // page pre-filtered — a catalog title without a TMDB id has no detail data.
  const openFavoriteMovie = useCallback((movie: VodItem) => {
    if (movie.tmdbId && Number.isInteger(movie.tmdbId) && movie.tmdbId > 0) {
      setAutoTrailer(false);
      setSelected({ id: movie.tmdbId, title: movie.rawTitle, media: "movie" });
    } else {
      navigate(`/movies?q=${encodeURIComponent(movie.rawTitle)}`);
    }
  }, [navigate])

  function tuneRecent(channel: RecentChannel) {
    const store = usePlayerStore.getState();
    store.pushRecent({ id: channel.id, name: channel.name, number: channel.number, streamUrl: channel.streamUrl, altStreamUrls: channel.altStreamUrls });
    void store.playSource({ url: channel.streamUrl, type: "live", title: channel.name, altUrls: channel.altStreamUrls, mirrorKey: mirrorKeyFor(activeProviderSourceId(), channel.id) });
  }

  async function playMatched(item: VodItem, tmdbId = item.tmdbId) {
    tracePlay("home: play pressed", item.rawTitle);
    const saved = useProgressStore.getState().get(item.id);
    const store = usePlayerStore.getState();
    store.setPlayingContent({ contentType: "vod", contentRef: item.id });
    useProgressStore.getState().remember(item.id, { title: item.rawTitle, posterUrl: item.posterUrl, url: item.streamUrl });
    // Other streams of this same film, tried if this one will not play (duplicates.ts).
    const alternates = await alternateStreamUrls(queryClient, item);
    tracePlay("home: other copies looked up", String(alternates.length));
    await store.playSource({
      url: item.streamUrl,
      type: "vod",
      title: item.rawTitle,
      artworkUrl: item.posterUrl,
      ...(alternates.length > 0 ? { altUrls: alternates } : {}),
    });
    // Home clicks don't have the TMDB runtime loaded (unlike the Movies
    // detail flow), so resolve it after playback starts and patch the source
    // — the seek bar then spans the whole title instead of the frontier.
    patchTranscodedRuntime(tmdbId, item.rawTitle, item.streamUrl);
    if (isResumable(saved)) store.seek(saved!.positionSeconds);
    setSelected(null);
  }

  function playContinue(entry: WatchProgressEntry) {
    const profileId = entry.profileId;
    const meta = useProgressStore.getState().metaFor(profileId, entry.contentRef);
    if (!meta || profileId !== useProfiles.getState().activeProfileId) return;
    const store = usePlayerStore.getState();
    store.setPlayingContent({ contentType: entry.contentType, contentRef: entry.contentRef });
    // The saved progress already knows the runtime — pass it straight through
    // so the seek bar is right from the first frame (no TMDB round-trip).
    void store
      .playSource({
        url: meta.url,
        type: "vod",
        title: meta.title,
        artworkUrl: meta.posterUrl,
        ...(entry.durationSeconds > 0 ? { durationSeconds: entry.durationSeconds } : {}),
        // A catch-up replay's saved length is the programme's own, so it stays
        // authoritative on resume just as it was on first play.
        ...(entry.contentType === "catchup" ? { durationIsExact: true } : {}),
      })
      .then(() => {
        if (isResumable(entry)) store.seek(entry.positionSeconds);
      });
  }

  // Stable, so a memoised rail only re-renders when its own items or focus change.
  const openTitle = useCallback((t: SelectedTitle) => { setAutoTrailer(false); setSelected(t); }, []);
  const openTrailer = useCallback((t: SelectedTitle) => { setAutoTrailer(true); setSelected(t); }, []);
  // Also stable: a fresh object here would defeat the rails' memo.
  const seeAll = useMemo(() => ({ label: "See all", href: media === "tv" ? "/series" : "/movies" }), [media]);

  // Each rail is built once per change to its OWN data. Home re-renders eight
  // times while a screenful of data arrives, and rebuilding every tile each
  // time is what made the projector crawl (2026-09-20).
  const recommendedRail = useMemo(() => (
    <>
          {showRecommended && (
            <Section title="Recommended For You">
              <Rail>
                {recommendedItems.map((recommendedItem: RecommendedItem, col) => {
                  const row = rowIndex("recommended");
                  const focused = isFocused(row, col);
                  const item = recommendedItem.entry.item as VodItem;
                  const name = recommendedItem.entry.label ?? displayTitle(item.rawTitle);
                  return (
                    <button
                      key={`${recommendedItem.side}:${item.id}`}
                      type="button"
                      className={cn("home-tile", focused && "home-tile--focused")}
                      data-home-row={row}
                      data-home-col={col}
                      tabIndex={focused ? 0 : -1}
                      onClick={() =>
                        recommendedItem.side === "vod"
                          ? openFavoriteMovie(item)
                          : navigate(`/series?q=${encodeURIComponent(item.rawTitle)}`)
                      }
                      aria-label={name}
                    >
                      {item.posterUrl ? (
                        <img className="home-tile__art" src={tilePoster(item.posterUrl)} alt="" loading="lazy" decoding="async" />
                      ) : (
                        <span className="home-tile__fallback">{tileLetter(item.rawTitle, "?")}</span>
                      )}
                      <TileRating value={item.rating} />
                      <span className="home-tile__label">{name}</span>
                    </button>
                  );
                })}
              </Rail>
            </Section>
          )}
    </>
  ), [showRecommended, recommendedItems, rowIndex, isFocused, openFavoriteMovie, navigate]);

  const myListRail = useMemo(() => (
    <>
          {showMyList && (
            <Section title="My List" action={{ label: "See all", href: "/mylist" }}>
              <Rail>
                {favMovies.map((movie, col) => {
                  const row = rowIndex("mylist");
                  const focused = isFocused(row, col);
                  return (
                    <button
                      key={`m:${movie.id}`}
                      type="button"
                      className={cn("home-tile", focused && "home-tile--focused")}
                      data-home-row={row}
                      data-home-col={col}
                      tabIndex={focused ? 0 : -1}
                      onClick={() => openFavoriteMovie(movie)}
                      aria-label={movie.rawTitle}
                    >
                      {movie.posterUrl ? (
                        <img className="home-tile__art" src={tilePoster(movie.posterUrl)} alt="" loading="lazy" decoding="async" />
                      ) : (
                        <span className="home-tile__fallback">{tileLetter(movie.rawTitle, "?")}</span>
                      )}
                      <TileRating value={movie.rating} />
                      <span className="home-tile__label">{movie.rawTitle}</span>
                    </button>
                  );
                })}
                {favSeries.map((item, i) => {
                  const col = favMovies.length + i;
                  const row = rowIndex("mylist");
                  const focused = isFocused(row, col);
                  return (
                    <button
                      key={`s:${item.id}`}
                      type="button"
                      className={cn("home-tile", focused && "home-tile--focused")}
                      data-home-row={row}
                      data-home-col={col}
                      tabIndex={focused ? 0 : -1}
                      onClick={() => navigate(`/series?q=${encodeURIComponent(item.rawTitle)}`)}
                      aria-label={item.rawTitle}
                    >
                      {item.posterUrl ? (
                        <img className="home-tile__art" src={tilePoster(item.posterUrl)} alt="" loading="lazy" decoding="async" />
                      ) : (
                        <span className="home-tile__fallback">{tileLetter(item.rawTitle, "?")}</span>
                      )}
                      <TileRating value={item.rating} />
                      <span className="home-tile__label">{item.rawTitle}</span>
                    </button>
                  );
                })}
                {favChannels.map((channel, i) => {
                  const col = favMovies.length + favSeries.length + i;
                  const row = rowIndex("mylist");
                  const focused = isFocused(row, col);
                  return (
                    <button
                      key={`c:${channel.id}`}
                      type="button"
                      className={cn("home-tile home-tile--live", focused && "home-tile--focused")}
                      data-home-row={row}
                      data-home-col={col}
                      tabIndex={focused ? 0 : -1}
                      onClick={() => tuneChannel(channel)}
                      aria-label={`Watch ${channel.name}`}
                    >
                      {channel.logoUrl ? (
                        <img className="home-tile__logo" src={channel.logoUrl} alt="" loading="lazy" />
                      ) : (
                        <span className="home-tile__fallback">{tileLetter(channel.name, "?")}</span>
                      )}
                      <span className="home-tile__channel">{channel.name}</span>
                    </button>
                  );
                })}
              </Rail>
            </Section>
          )}
    </>
  ), [showMyList, favMovies, favSeries, favChannels, rowIndex, isFocused, openFavoriteMovie, navigate, tuneChannel]);

  const liveRail = useMemo(() => (
    <>
          {liveNow.tiles.length > 0 && (
            <Section title="Live Now" action={{ label: "TV guide data", href: "/live" }}>
              <Rail className="home-live-rail">
                {liveTiles.map((tile, col) => {
                  const row = rowIndex("live");
                  const focused = isFocused(row, col);
                  const start = new Date(tile.start).getTime();
                  const end = new Date(tile.end).getTime();
                  const elapsed = Math.min(Math.max((Date.now() - start) / Math.max(1, end - start), 0), 1);
                  return (
                    <button
                      key={tile.channel.id}
                      type="button"
                      className={cn("home-tile home-tile--live", focused && "home-tile--focused")}
                      data-home-row={row}
                      data-home-col={col}
                      tabIndex={focused ? 0 : -1}
                      onClick={() => tuneChannel(tile.channel)}
                      aria-label={`Watch ${tile.showName} on ${tile.channel.name}`}
                    >
                      <LiveTileArt tile={tile} />
                      <span className="home-tile__channel">{tile.channel.name}</span>
                      <span className="home-tile__time">{formatProgramRange(tile.start, tile.end)}</span>
                      <span className="home-tile__program">{tile.showName}{tile.episodeName ? ` · ${tile.episodeName}` : ""}</span>
                      <span className="home-tile__progress"><span style={{ width: `${elapsed * 100}%` }} /></span>
                    </button>
                  );
                })}
              </Rail>
            </Section>
          )}
    </>
  ), [liveNow.tiles.length, liveTiles, rowIndex, isFocused, tuneChannel]);

  const continueRail = useMemo(() => (
    <>
          {continueEntries.length > 0 && (
            <Section title="Continue Watching">
              <Rail>
                {continueEntries.map((entry, col) => {
                  const meta = playbackMeta[progressKey(activeProfile, entry.contentRef)];
                  const tmdbArt = artFor(continueArtRequests[col]);
                  const row = rowIndex("continue");
                  const focused = isFocused(row, col);
                  // Progress bar runs through the same ticking clock as the other
                  // rails (`now` is a dep of continueEntries above), so the row
                  // re-renders on the minute tick and the bar/label stay in sync.
                  const pct = entry.durationSeconds > 0
                    ? Math.min(100, Math.max(0, (entry.positionSeconds / entry.durationSeconds) * 100))
                    : 0;
                  return (
                    <button
                      key={entry.contentRef}
                      type="button"
                      className={cn("home-tile", focused && "home-tile--focused")}
                      data-home-row={row}
                      data-home-col={col}
                      tabIndex={focused ? 0 : -1}
                      onClick={() => playContinue(entry)}
                      aria-label={`Resume ${meta?.title ?? entry.contentRef}`}
                    >
                      <StepDownArt
                        // A film or episode keeps the picture saved when it was played,
                        // TMDB's as the backstop. A replay's saved picture is only its
                        // channel's logo, so the programme's own artwork comes first.
                        candidates={
                          entry.contentType === "catchup"
                            ? [{ src: tmdbArt }, { src: meta?.posterUrl, logo: true }]
                            : [{ src: meta?.posterUrl }, { src: tmdbArt }]
                        }
                        fallback={tileLetter(meta?.title, "▶")}
                      />
                      <span className="home-tile__label">{meta?.title ?? "Continue"}</span>
                      <span className="home-tile__badge">● Resume</span>
                      {pct > 0 && (
                        <>
                          <span className="home-tile__progress"><span style={{ width: `${pct}%` }} /></span>
                          <span className="home-tile__pct">{Math.round(pct)}% watched</span>
                        </>
                      )}
                    </button>
                  );
                })}
              </Rail>
            </Section>
          )}
    </>
  ), [continueEntries, continueArtRequests, artFor, rowIndex, isFocused, now, playMatched, navigate, playbackMeta, activeProfile]);

  const recentRail = useMemo(() => (
    <>
          {recentRows.length > 0 && (
            <Section title="Recently Watched">
              <Rail>
                {recentRows.map(({ channel, program }, col) => {
                  const row = rowIndex("recent");
                  const focused = isFocused(row, col);
                  const start = program ? new Date(program.start).getTime() : 0;
                  const end = program ? new Date(program.end).getTime() : 0;
                  const elapsed = program ? Math.min(Math.max((Date.now() - start) / Math.max(1, end - start), 0), 1) : 0;
                  return (
                    <button
                      key={channel.id}
                      type="button"
                      className={cn("home-tile home-tile--live", focused && "home-tile--focused")}
                      data-home-row={row}
                      data-home-col={col}
                      tabIndex={focused ? 0 : -1}
                      onClick={() => tuneRecent(channel)}
                      aria-label={`Watch ${channel.name}${program ? ` — ${program.title}` : ""}`}
                    >
                      {/* Same order as Live Now: what is on, then the channel's logo. */}
                      <StepDownArt
                        candidates={[{ src: artFor(recentArtRequests[col]) }, { src: channel.logoUrl, logo: true }]}
                        fallback={tileLetter(channel.name, "?")}
                      />
                      <span className="home-tile__channel">{channel.name}</span>
                      {program && <span className="home-tile__program">{program.title}</span>}
                      {program && (
                        <span className="home-tile__progress"><span style={{ width: `${elapsed * 100}%` }} /></span>
                      )}
                    </button>
                  );
                })}
              </Rail>
            </Section>
          )}
    </>
  ), [recentRows, recentArtRequests, artFor, rowIndex, isFocused, now, tuneChannel]);

  const providerMissing = isProviderNotConfigured(channelsQuery.error) || isProviderNotConfigured(vod.error);

  return (
    <div className="page page--home" data-home>
      {providerMissing && <ProviderSetupPrompt />}
      <section className={`hero ${hasHero ? "hero--loaded" : ""}`}>
        {/* Preload the NEXT backdrop so the minute rotation swaps without a blank flash. */}
        {nextHeroBackdrop && <link rel="preload" as="image" href={nextHeroBackdrop} />}
        {/* Keyed backdrop layer — remounts per title and fades in over the
            previous one, so the rotation crossfades instead of snapping. */}
        {heroBackdrop && <div key={`backdrop-${heroTitle!.id}`} className="hero__backdrop" style={{ backgroundImage: `url(${heroBackdrop})` }} />}
        {heroTrailerEmbed && <HeroTrailer src={heroTrailerEmbed} />}
        <div className="hero__overlay" />
        <div className="hero__info" key={`info-${heroTitle?.id ?? "empty"}`}>
          {hasHero ? (
            <>
              <p className="hero__overline"><span className="hero__dot" /> Featured</p>
              <h1 className="hero__title">{heroTitle!.title}</h1>
              <p className="hero__meta">{heroYear && <span>{heroYear}</span>}{heroRating ? <span>★ {heroRating.toFixed(1)}</span> : null}</p>
              <p className="hero__desc">{heroTitle!.overview?.slice(0, 180)}{(heroTitle!.overview?.length ?? 0) > 180 ? "…" : ""}</p>
              <div className="hero__actions">
                <Button variant="primary" data-home-row={rowIndex("hero")} data-home-col={0} className={isFocused(rowIndex("hero"), 0) ? "home-focused" : undefined} onClick={() => void playMatched(heroItem!, heroTitle!.id)}>
                  ▶ Play Now
                </Button>
                {/* Column 1 of the hero row — the roving focus counts three
                    columns whenever there is a trailer, and finds its target
                    by these attributes. Without them Right from Play Now moved
                    the cursor to a column with no element: the ring stayed put
                    (a press that did nothing) and the next press went straight
                    to Open Guide (owner, 2026-09-22). */}
                {heroTrailerUrl && (
                  <TrailerButton
                    trailerUrl={heroTrailerUrl}
                    label="▶ Trailer"
                    buttonProps={{
                      "data-home-row": rowIndex("hero"),
                      "data-home-col": 1,
                      className: isFocused(rowIndex("hero"), 1) ? "home-focused" : undefined,
                    }}
                  />
                )}
                <Button variant="ghost" data-home-row={rowIndex("hero")} data-home-col={heroTrailerUrl ? 2 : 1} className={isFocused(rowIndex("hero"), heroTrailerUrl ? 2 : 1) ? "home-focused" : undefined} onClick={() => navigate("/live")}>
                  Open Guide
                </Button>
              </div>
            </>
          ) : (
            <>
              <p className="hero__overline"><span className="hero__dot" /> Live Now</p>
              <h1 className="hero__title">{brand().appName}</h1>
              <p className="hero__desc">Your channels, movies, and series — one clean place.</p>
              <div className="hero__actions">
                <Button variant="primary" data-home-row={rowIndex("hero")} data-home-col={0} className={isFocused(rowIndex("hero"), 0) ? "home-focused" : undefined} onClick={() => navigate("/live")}>
                  ▶ Watch Live
                </Button>
                <Button variant="ghost" data-home-row={rowIndex("hero")} data-home-col={1} className={isFocused(rowIndex("hero"), 1) ? "home-focused" : undefined} onClick={() => navigate("/live")}>
                  Open Guide
                </Button>
              </div>
            </>
          )}
        </div>
      </section>

      {launcherHome && <LauncherRows home={launcherHome} appsRow={rowIndex("apps")} isFocused={isFocused} />}

      {recommendedRail}
      {myListRail}
      {liveRail}

      {liveNow.tiles.length === 0 && liveNow.isLoading && (
        <Section title="Live Now">
          <Rail>
            {Array.from({ length: 6 }, (_, i) => <Skeleton key={i} className="home-tile__skeleton" />)}
          </Rail>
        </Section>
      )}

      {continueRail}
      {recentRail}

      <div className="home-media-toggle" role="group" aria-label="Show movies or series">
        {(["movie", "tv"] as const).map((option, col) => {
          const row = rowIndex("media");
          const active = media === option;
          const focused = isFocused(row, col);
          return (
            <button
              key={option}
              type="button"
              className={cn("home-media-toggle__btn", active && "home-media-toggle__btn--active", focused && "home-focused")}
              data-home-row={row}
              data-home-col={col}
              tabIndex={focused || active ? 0 : -1}
              aria-pressed={active}
              onClick={() => { setMedia(option); focusAt(row, col); }}
            >
              {option === "movie" ? "Movies" : "Series"}
            </button>
          );
        })}
      </div>

      <TmdbRail
        row={rowIndex("trending")}
        media={media}
        title={media === "tv" ? "Trending Series" : "Trending Movies"}
        action={seeAll}
        items={filteredTrending}
        loading={trending.isLoading}
        focusedCol={focusedColOf(rowIndex("trending"))}
        tiles={tilesPerRow}
        focusAt={focusAt}
        onOpen={openTitle}
        onTrailer={openTrailer}
      />
      <TmdbRail
        row={rowIndex("popular")}
        media={media}
        title={media === "tv" ? "Popular Series" : "Popular Movies"}
        action={seeAll}
        items={filteredPopular}
        loading={popular.isLoading}
        focusedCol={focusedColOf(rowIndex("popular"))}
        tiles={tilesPerRow}
        focusAt={focusAt}
        onOpen={openTitle}
        onTrailer={openTrailer}
      />
      {shownShelves.map((row) => (
        <TmdbRail
          key={`${media}-${row.shelf}`}
          row={rowIndex(`shelf-${row.shelf}`)}
          media={media}
          title={row.title}
          items={row.items}
          loading={false}
          focusedCol={focusedColOf(rowIndex(`shelf-${row.shelf}`))}
          tiles={tilesPerRow}
          focusAt={focusAt}
          onOpen={openTitle}
          onTrailer={openTrailer}
        />
      ))}
      {shownSpotlight && (
        <TmdbRail
          key={`${media}-spotlight`}
          row={rowIndex("spotlight")}
          media={media}
          title={shownSpotlight.title}
          items={shownSpotlight.items}
          loading={false}
          focusedCol={focusedColOf(rowIndex("spotlight"))}
          tiles={tilesPerRow}
          focusAt={focusAt}
          onOpen={openTitle}
          onTrailer={openTrailer}
        />
      )}

      <TitleDetail
        open={selected !== null}
        onClose={() => { setAutoTrailer(false); setSelected(null); }}
        autoPlayTrailer={autoTrailer}
        title={selected?.title ?? ""}
        subtitle={movieFields?.subtitle ?? details.data?.release_date?.slice(0, 4)}
        overview={movieFields?.overview ?? details.data?.overview}
        posterUrl={IMG(details.data?.poster_path ?? selected?.posterPath, "w342") ?? matchedItem?.posterUrl ?? matchedSeries?.posterUrl}
        backdropUrl={IMG(details.data?.backdrop_path ?? selected?.backdropPath, "w780") ?? movieFields?.backdropUrl}
        rating={movieFields?.rating ?? details.data?.vote_average}
        genres={movieFields?.genres ?? details.data?.genres?.map((g) => g.name)}
        director={movieFields?.director}
        trailerUrl={movieFields?.trailerUrl ?? matchedSeries?.trailerUrl ?? firstTrailerUrl(details.data)}
        status={
          matchedItem || matchedSeries
            ? { kind: "streamable", detail: "In your playlist" }
            : vod.isLoading
              ? { kind: "loading" }
              : { kind: "discovery", detail: "Discovery title — not matched to your playlist" }
        }
        onPlay={
          matchedItem
            ? () => void playMatched(matchedItem, selected!.id)
            : matchedSeries
              // A series has no single thing to play: open it on the Series
              // page, where its seasons and episodes are.
              ? () => { setSelected(null); navigate(`/series?q=${encodeURIComponent(matchedSeries.rawTitle)}`); }
              : undefined
        }
        playLabel={
          matchedSeries
            ? "▶ Episodes"
            : matchedItem && isResumable(useProgressStore.getState().get(matchedItem.id)) ? "▶ Resume" : "▶ Play"
        }
      />
    </div>
  );
}

interface TmdbRailProps {
  row: number;
  media: HomeMedia;
  title: string;
  action?: { label: string; href: string };
  items: TmdbSummaryShape[];
  loading: boolean;
  /** The focused column when the D-pad is on THIS row, else -1. A number, not a
   * callback over the whole page, so a key press re-renders the row it left and
   * the row it entered — not every rail on the page. On the 1 GB projector
   * re-rendering all ten rails took over two seconds per press (2026-09-20). */
  focusedCol: number;
  /** How many tiles this row shows (Settings > Home). */
  tiles: number;
  focusAt: (row: number, col: number) => void;
  onOpen: (title: SelectedTitle) => void;
  /** Open the detail with the trailer already playing. */
  onTrailer: (title: SelectedTitle) => void;
}

// NOTE: `focusAt` is accepted but not yet called here, so clicking or hovering
// a tile in a TMDB rail does not move the D-pad focus cursor to it. The prop
// stays in the contract because that wiring is the intended behaviour.
const TmdbRail = memo(function TmdbRail({ row, media, title, action, items, loading, focusedCol, tiles, onOpen, onTrailer }: TmdbRailProps) {
  return (
    <Section title={title} action={action}>
      <Rail>
        {loading && Array.from({ length: 6 }, (_, i) => <Skeleton key={i} className="home-tile__skeleton" />)}
        {items.slice(0, tiles).map((item, col) => {
          const focused = col === focusedCol;
          const poster = IMG(item.poster_path, "w342") ?? item.posterUrl;
          const selected = { id: item.id, title: item.title, media, posterPath: item.poster_path ?? undefined, backdropPath: item.backdrop_path ?? undefined };
          return (
            <div key={item.id} className="home-tile-wrap">
              <button
                type="button"
                className={cn("home-tile", focused && "home-tile--focused")}
                data-home-row={row}
                data-home-col={col}
                tabIndex={focused ? 0 : -1}
                onClick={() => onOpen(selected)}
                aria-label={item.title}
              >
                {poster ? (
                  <img className="home-tile__art" src={tilePoster(poster)} alt="" loading="lazy" decoding="async" />
                ) : (
                  <span className="home-tile__fallback">{tileLetter(item.title, "?")}</span>
                )}
                <TileRating value={item.vote_average} />
                <span className="home-tile__label">{item.title}</span>
              </button>
              {/* Hover-only shortcut (mouse) — TV users get the trailer from the
                  detail overlay, keeping the D-pad grid at one target per tile. */}
              <button
                type="button"
                className="home-tile__trailer"
                onClick={() => onTrailer(selected)}
                aria-label={`Watch trailer for ${item.title}`}
              >
                ▶ Trailer
              </button>
            </div>
          );
        })}
      </Rail>
    </Section>
  );
});
